import React, { useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../ui/accordion';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import {
  AlertCircle, AlertTriangle, Car, BatteryCharging, Play, Loader2, RotateCcw, CarFront, Cog, SlidersHorizontal, Route,
} from 'lucide-react';
import {
  VEHICLE_FIELD_SPECS, AWD_SPLIT_SPEC, MOTOR_FIELD_SPECS, BATTERY_FIELD_SPECS, BLENDING_FIELD_SPECS, PARALLEL_RATIO_SPEC,
  DRIVE_LAYOUT_OPTIONS, STRATEGY_OPTIONS, DRIVE_CYCLE_OPTIONS, DRIVE_TECHNOLOGY_OPTIONS, CHEMISTRY_OPTIONS,
  SAMPLING_TIME_OPTIONS_S, DEFAULT_SAMPLING_TIME_S, SIMULATION_TIME_SPEC, DEFAULT_BLENDING,
  REGEN_PRESETS, DEFAULT_PRESET_ID, findPreset, validateField, validateCrossFields, toRawStrings,
} from '../../lib/regenBrakingTools';
import { simulationApi } from '../../lib/simulationApi';
import RegenSummaryPanel from './simulator/RegenSummaryPanel';
import {
  BrakingForceSplitChart, RegenPowerChart, SocTempChart, LimiterTimelineChart, CumulativeEnergyChart, IdealDistributionChart,
} from './simulator/RegenBrakingCharts';

const FieldInput = ({ spec, raw, error, onChange, testPrefix, placeholder }) => (
  <div className="space-y-1">
    <Label htmlFor={`${testPrefix}-${spec.key}`} className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 leading-tight">
      {spec.label} <span className="normal-case text-muted-foreground/70">({spec.unit})</span>
    </Label>
    <Input
      id={`${testPrefix}-${spec.key}`} type="number" step="any" value={raw ?? ''} placeholder={placeholder}
      onChange={(e) => onChange(spec.key, e.target.value)} className="font-mono h-8 text-sm"
      aria-invalid={!!error} aria-describedby={error ? `${testPrefix}-${spec.key}-error` : undefined}
      data-testid={`${testPrefix}-${spec.key}-input`}
    />
    {error && (
      <p id={`${testPrefix}-${spec.key}-error`} className="flex items-center gap-1 text-xs text-destructive" data-testid={`${testPrefix}-${spec.key}-error`}>
        <AlertCircle className="h-3 w-3 shrink-0" /> {error}
      </p>
    )}
  </div>
);

const SelectField = ({ label, value, onChange, options, testId }) => (
  <div className="space-y-1">
    <Label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground leading-tight">{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 text-sm" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const GRID = 'grid sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3';
// One pass of each cycle (s) — the event charts open zoomed to this window on long runs.
const CYCLE_LENGTH_S = { wltc: 1800, midc: 1179, us06: 600, max_regen: 300 };

function validateGroup(raw, specs) {
  const errors = {};
  const values = {};
  for (const spec of specs) {
    const err = validateField(spec, raw[spec.key]);
    if (err) errors[spec.key] = err;
    else values[spec.key] = Number(raw[spec.key]);
  }
  return { errors, values };
}

const Section = ({ value, icon: Icon, title, children }) => (
  <AccordionItem value={value}>
    <AccordionTrigger className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground py-2">
      <span className="flex items-center gap-2"><Icon className="h-4 w-4" /> {title}</span>
    </AccordionTrigger>
    <AccordionContent className="space-y-3 pb-3 pt-0">{children}</AccordionContent>
  </AccordionItem>
);

const RegenBrakingSimulator = () => {
  const initial = findPreset(DEFAULT_PRESET_ID);
  const [presetId, setPresetId] = useState(DEFAULT_PRESET_ID);
  const [vehicleRaw, setVehicleRaw] = useState(() => toRawStrings(initial.vehicle));
  const [motorRaw, setMotorRaw] = useState(() => toRawStrings(initial.motor));
  const [batteryRaw, setBatteryRaw] = useState(() => toRawStrings(initial.battery));
  const [blendingRaw, setBlendingRaw] = useState(() => toRawStrings(DEFAULT_BLENDING));
  const [driveCycle, setDriveCycle] = useState(initial.drive_cycle);
  const [samplingTimeS, setSamplingTimeS] = useState(DEFAULT_SAMPLING_TIME_S);
  // Blank = range run to Minimum SoC (same convention as the EV Powertrain Simulator);
  // after each run it echoes the actual simulated time back.
  const [simulationTimeRaw, setSimulationTimeRaw] = useState('');
  const preset = findPreset(presetId);

  const loadPreset = (id) => {
    const p = findPreset(id);
    if (!p) return;
    setPresetId(id);
    setVehicleRaw(toRawStrings(p.vehicle));
    setMotorRaw(toRawStrings(p.motor));
    setBatteryRaw(toRawStrings(p.battery));
    setDriveCycle(p.drive_cycle);
    setSimulationTimeRaw('');
  };
  const presetModified = !!preset && JSON.stringify({ v: vehicleRaw, m: motorRaw, b: batteryRaw })
    !== JSON.stringify({ v: toRawStrings(preset.vehicle), m: toRawStrings(preset.motor), b: toRawStrings(preset.battery) });

  const setter = (fn) => (key, value) => fn((prev) => ({ ...prev, [key]: value }));
  const setVehicleField = setter(setVehicleRaw);
  const setMotorField = setter(setMotorRaw);
  const setBatteryField = setter(setBatteryRaw);
  const setBlendingField = setter(setBlendingRaw);

  const isAwd = vehicleRaw.drive_layout === 'awd';
  const isParallel = blendingRaw.strategy === 'parallel';

  const groups = useMemo(() => {
    const v = validateGroup(vehicleRaw, isAwd ? [...VEHICLE_FIELD_SPECS, AWD_SPLIT_SPEC] : VEHICLE_FIELD_SPECS);
    const m = validateGroup(motorRaw, MOTOR_FIELD_SPECS);
    const b = validateGroup(batteryRaw, BATTERY_FIELD_SPECS);
    const bl = validateGroup(blendingRaw, isParallel ? [...BLENDING_FIELD_SPECS, PARALLEL_RATIO_SPEC] : BLENDING_FIELD_SPECS);
    v.values.drive_layout = vehicleRaw.drive_layout;
    if (!isAwd) v.values.awd_front_regen_pct = Number(vehicleRaw.awd_front_regen_pct) || 50;
    m.values.drive_technology = motorRaw.drive_technology;
    b.values.chemistry = batteryRaw.chemistry;
    bl.values.strategy = blendingRaw.strategy;
    if (!isParallel) bl.values.parallel_regen_pct = Number(blendingRaw.parallel_regen_pct) || 70;
    const cross = validateCrossFields(v.values, b.values, bl.values);
    Object.assign(v.errors, cross.vehicle);
    Object.assign(b.errors, cross.battery);
    Object.assign(bl.errors, cross.blending);
    return { vehicle: v, motor: m, battery: b, blending: bl };
  }, [vehicleRaw, motorRaw, batteryRaw, blendingRaw, isAwd, isParallel]);
  const simulationTimeError = useMemo(() => validateField(SIMULATION_TIME_SPEC, simulationTimeRaw), [simulationTimeRaw]);
  const hasErrors = Object.values(groups).some((g) => Object.keys(g.errors).length > 0) || !!simulationTimeError;

  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState(null);
  const [result, setResult] = useState(null);
  const [resultCycle, setResultCycle] = useState(null);
  // Stale = inputs differ from the snapshot taken after the last successful run (the run
  // itself writes Simulation Time back, which must not mark its own results stale).
  const snapshotRef = useRef(null);
  const snapshot = (simTime) => JSON.stringify({ vehicleRaw, motorRaw, batteryRaw, blendingRaw, driveCycle, samplingTimeS, simTime });
  const stale = !!result && snapshotRef.current !== null && snapshotRef.current !== snapshot(simulationTimeRaw);

  const runSimulation = async () => {
    setRunning(true);
    setRunError(null);
    try {
      const response = await simulationApi.runRegenBlending({
        vehicle: groups.vehicle.values,
        motor: groups.motor.values,
        battery: groups.battery.values,
        blending: groups.blending.values,
        drive_cycle: driveCycle,
        simulation_time_s: simulationTimeRaw === '' ? null : Number(simulationTimeRaw),
        sampling_time_s: samplingTimeS,
      });
      setResult(response);
      setResultCycle(driveCycle);
      const actual = String(response.summary.simulated_time_s);
      setSimulationTimeRaw(actual);
      snapshotRef.current = snapshot(actual);
    } catch (e) {
      setRunError(e.message);
    }
    setRunning(false);
  };

  return (
    <div className="space-y-6">
      <Card className="technical-card">
        <CardHeader className="p-4 pb-2">
          <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2 text-base">
            <Car className="h-5 w-5 text-autoxium-blue" />
            Regenerative Braking Blending Simulator
          </CardTitle>
          <CardDescription className="text-xs">
            Pick a vehicle, blending strategy and drive cycle, then submit to split every braking
            step between regen and friction brakes and compare range with and without regen.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 pt-0 space-y-1">
          <div className="rounded-md border border-border/60 bg-muted/30 p-3 mb-2 space-y-2" data-testid="regen-preset-panel">
            <div className="flex flex-col sm:flex-row sm:items-end gap-2">
              <div className="space-y-1 flex-1 min-w-0">
                <Label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground leading-tight flex items-center gap-1.5">
                  <CarFront className="h-3.5 w-3.5" /> Vehicle Preset
                </Label>
                <Select value={presetId} onValueChange={loadPreset}>
                  <SelectTrigger className="h-8 text-sm" data-testid="regen-preset-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REGEN_PRESETS.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {presetModified && (
                <Button variant="outline" size="sm" className="h-8 shrink-0" onClick={() => loadPreset(presetId)} data-testid="regen-preset-reset">
                  <RotateCcw className="h-3.5 w-3.5" /> Reset to preset
                </Button>
              )}
            </div>
            {preset && (
              <p className="text-xs text-muted-foreground" data-testid="regen-preset-note">
                {preset.note}
                {presetModified && <span className="ml-2 text-amber-600 dark:text-amber-400">(inputs modified)</span>}
              </p>
            )}
          </div>

          <Accordion type="multiple" defaultValue={['cycle', 'blending', 'vehicle', 'motor', 'battery']}>
            <Section value="cycle" icon={Route} title="Drive Cycle">
              <div className={GRID}>
                <SelectField label="Drive Cycle" value={driveCycle} onChange={setDriveCycle} options={DRIVE_CYCLE_OPTIONS} testId="regen-cycle-select" />
                <div className="space-y-1">
                  <Label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground leading-tight">Sampling Time (s)</Label>
                  <Select value={String(samplingTimeS)} onValueChange={(v) => setSamplingTimeS(Number(v))}>
                    <SelectTrigger className="h-8 text-sm" data-testid="regen-sampling-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SAMPLING_TIME_OPTIONS_S.map((s) => <SelectItem key={s} value={String(s)}>{s.toFixed(1)} s</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <FieldInput spec={SIMULATION_TIME_SPEC} raw={simulationTimeRaw} error={simulationTimeError}
                  onChange={(_k, value) => setSimulationTimeRaw(value)} testPrefix="regen" placeholder="Blank = run to Min. SoC" />
              </div>
              <p className="text-xs text-muted-foreground">
                WLTC and MIDC never brake harder than about 0.2 g; US06 reaches about 0.3 g. The
                5-minute Max Regen cycle brakes at 0.1 to 0.8 g so every limit — including axle
                stability — gets a chance to bind.
              </p>
            </Section>

            <Section value="blending" icon={SlidersHorizontal} title="Blending Strategy">
              <div className={GRID}>
                <SelectField label="Strategy" value={blendingRaw.strategy} onChange={(v) => setBlendingField('strategy', v)} options={STRATEGY_OPTIONS} testId="regen-strategy-select" />
                {isParallel && (
                  <FieldInput spec={PARALLEL_RATIO_SPEC} raw={blendingRaw.parallel_regen_pct} error={groups.blending.errors.parallel_regen_pct} onChange={setBlendingField} testPrefix="regen-blending" />
                )}
                {BLENDING_FIELD_SPECS.map((spec) => (
                  <FieldInput key={spec.key} spec={spec} raw={blendingRaw[spec.key]} error={groups.blending.errors[spec.key]} onChange={setBlendingField} testPrefix="regen-blending" />
                ))}
              </div>
            </Section>

            <Section value="vehicle" icon={Car} title="Vehicle & Chassis">
              <div className={GRID}>
                <SelectField label="Drive Layout" value={vehicleRaw.drive_layout} onChange={(v) => setVehicleField('drive_layout', v)} options={DRIVE_LAYOUT_OPTIONS} testId="regen-layout-select" />
                {isAwd && (
                  <FieldInput spec={AWD_SPLIT_SPEC} raw={vehicleRaw.awd_front_regen_pct} error={groups.vehicle.errors.awd_front_regen_pct} onChange={setVehicleField} testPrefix="regen-vehicle" />
                )}
                {VEHICLE_FIELD_SPECS.map((spec) => (
                  <FieldInput key={spec.key} spec={spec} raw={vehicleRaw[spec.key]} error={groups.vehicle.errors[spec.key]} onChange={setVehicleField} testPrefix="regen-vehicle" />
                ))}
              </div>
            </Section>

            <Section value="motor" icon={Cog} title="Motor">
              <div className={GRID}>
                <SelectField label="Motor & Inverter Technology" value={motorRaw.drive_technology} onChange={(v) => setMotorField('drive_technology', v)} options={DRIVE_TECHNOLOGY_OPTIONS} testId="regen-tech-select" />
                {MOTOR_FIELD_SPECS.map((spec) => (
                  <FieldInput key={spec.key} spec={spec} raw={motorRaw[spec.key]} error={groups.motor.errors[spec.key]} onChange={setMotorField} testPrefix="regen-motor" />
                ))}
              </div>
            </Section>

            <Section value="battery" icon={BatteryCharging} title="Battery">
              <div className={GRID}>
                <SelectField label="Cell Chemistry" value={batteryRaw.chemistry} onChange={(v) => setBatteryField('chemistry', v)} options={CHEMISTRY_OPTIONS} testId="regen-chemistry-select" />
                {BATTERY_FIELD_SPECS.map((spec) => (
                  <FieldInput key={spec.key} spec={spec} raw={batteryRaw[spec.key]} error={groups.battery.errors[spec.key]} onChange={setBatteryField} testPrefix="regen-battery" />
                ))}
              </div>
            </Section>
          </Accordion>

          <div className="pt-3">
            <Button onClick={runSimulation} disabled={hasErrors || running} className="w-full" data-testid="run-regen-simulation-button">
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Submit &amp; Run Simulation
            </Button>
            {runError && (
              <div className="flex items-center gap-2 text-sm text-destructive mt-3" data-testid="regen-run-error">
                <AlertCircle className="h-4 w-4 shrink-0" /> {runError}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {result && (
        <div className={`space-y-6 transition-opacity ${stale ? 'opacity-50 pointer-events-none' : ''}`} data-testid="regen-results">
          {stale && (
            <Alert data-testid="regen-stale-banner" className="pointer-events-auto">
              <RotateCcw className="h-4 w-4" />
              <AlertDescription>Inputs have changed — submit again to update these results.</AlertDescription>
            </Alert>
          )}
          {result.warnings?.length > 0 && (
            <Alert data-testid="regen-warnings" className="border-amber-500/50">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <AlertDescription>
                <p className="font-semibold mb-1">The simulation ran, but check these points:</p>
                <ul className="list-disc pl-5 space-y-1 text-sm">
                  {result.warnings.map((w) => <li key={w}>{w}</li>)}
                </ul>
              </AlertDescription>
            </Alert>
          )}
          <RegenSummaryPanel summary={result.summary} limiterShares={result.limiter_shares} />
          <BrakingForceSplitChart key={`BrakingForceSplitChart-${result.summary.simulated_time_s}-${resultCycle}`} series={result.series} windowS={CYCLE_LENGTH_S[resultCycle]} />
          <RegenPowerChart key={`RegenPowerChart-${result.summary.simulated_time_s}-${resultCycle}`} series={result.series} windowS={CYCLE_LENGTH_S[resultCycle]} />
          <LimiterTimelineChart key={`LimiterTimelineChart-${result.summary.simulated_time_s}-${resultCycle}`} series={result.series} windowS={CYCLE_LENGTH_S[resultCycle]} />
          <SocTempChart series={result.series} />
          <IdealDistributionChart idealCurve={result.ideal_curve} operatingPoints={result.operating_points} />
          <CumulativeEnergyChart series={result.series} />
        </div>
      )}

      {!result && (
        <Card className="technical-card">
          <CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid="regen-empty-state">
            Fill in the inputs above and submit to see results and charts.
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RegenBrakingSimulator;
